import type {
  AssessedRiskRating,
  OptionPoints,
  RiskAssessmentQuestion,
  RiskAssessmentResponse,
  RiskAssessmentSection,
  RiskAssessmentSectionScore,
  RiskBand
} from '@gcv/shared';
import { CustomFieldType } from '@gcv/shared';

const SCORE_MIN = 0;
const SCORE_MAX = 100;

/** Section weights, and question weights within a section, each total this. */
export const REQUIRED_WEIGHT_TOTAL = 100;

/** Fallback weight for a scored question that carries none. */
const DEFAULT_QUESTION_WEIGHT = 1;

// option_points/weight live on the radio/select subtypes, not the base type.
const optionPointsOf = (field: RiskAssessmentQuestion): OptionPoints | undefined =>
  (field as { option_points?: OptionPoints }).option_points;

const weightOf = (field: RiskAssessmentQuestion): number | undefined => (field as { weight?: number }).weight;

/** Only radio/select fields carrying points contribute to the score. */
export const isScoredQuestion = (field: RiskAssessmentQuestion): boolean =>
  (field?.type === CustomFieldType.Radio || field?.type === CustomFieldType.Select) &&
  Object.keys(optionPointsOf(field) ?? {}).length > 0;

// Multi-select scores on its highest-scoring option, not the sum.
const pointsForResponse = (optionPoints: OptionPoints, response: RiskAssessmentResponse): number => {
  if (Array.isArray(response)) {
    return response.reduce((max, option) => Math.max(max, optionPoints[option] ?? 0), 0);
  }
  if (typeof response === 'string') {
    return optionPoints[response] ?? 0;
  }
  return 0;
};

const round2 = (value: number): number => Math.round(value * 100) / 100;

export interface RiskAssessmentScore {
  section_scores: RiskAssessmentSectionScore[];
  computed_score: number;
}

/**
 * Client-side mirror of the backend scoring, for the live score. Two-level weighting:
 * `(points_awarded / points_possible) * section weight`, summed to 0-100.
 * Sections with no scored points are dropped
 */
export const scoreRiskAssessment = (
  sections: RiskAssessmentSection[] | undefined,
  responses: { [field_id: string]: RiskAssessmentResponse } | undefined
): RiskAssessmentScore => {
  const answers = responses ?? {};
  const sectionScores: RiskAssessmentSectionScore[] = [];
  let total = 0;

  (sections ?? []).forEach((section) => {
    const weight = section.weight ?? 0;
    let pointsAwarded = 0;
    let pointsPossible = 0;

    (section.fields ?? []).filter(isScoredQuestion).forEach((field) => {
      const optionPoints = optionPointsOf(field) as OptionPoints;
      const questionWeight = weightOf(field) ?? DEFAULT_QUESTION_WEIGHT;

      pointsPossible += Math.max(...Object.values(optionPoints)) * questionWeight;
      pointsAwarded += pointsForResponse(optionPoints, answers[field.id]) * questionWeight;
    });

    const weightedScore = pointsPossible > 0 ? (pointsAwarded / pointsPossible) * weight : 0;
    total += weightedScore;

    sectionScores.push({
      section_id: section.id,
      label: section.label,
      weight,
      points_awarded: round2(pointsAwarded),
      points_possible: round2(pointsPossible),
      weighted_score: round2(weightedScore)
    });
  });

  return { section_scores: sectionScores, computed_score: round2(total) };
};

export interface RiskAssessmentSectionWeight {
  section_id: string;
  label: string;
  /** The section's own share of the 0-100 total. */
  weight: number;
  question_weight_total: number;
  has_scored_questions: boolean;
  /** True when question weights total 100 for sections with scored questions. */
  is_balanced: boolean;
}

export interface RiskAssessmentWeightValidation {
  section_weight_total: number;
  is_section_weight_balanced: boolean;
  sections: RiskAssessmentSectionWeight[];
}

/** Reports section-weight and per-section question-weight totals + balanced flags for the scoring rail. */
export const validateTemplateWeights = (
  sections: RiskAssessmentSection[] | undefined
): RiskAssessmentWeightValidation => {
  const sectionWeights = (sections ?? []).map((section) => {
    const scored = (section.fields ?? []).filter(isScoredQuestion);
    const questionWeightTotal = scored.reduce((sum, field) => sum + (weightOf(field) ?? 0), 0);
    const hasScoredQuestions = scored.length > 0;

    return {
      section_id: section.id,
      label: section.label,
      weight: section.weight ?? 0,
      question_weight_total: questionWeightTotal,
      has_scored_questions: hasScoredQuestions,
      is_balanced: hasScoredQuestions && questionWeightTotal === REQUIRED_WEIGHT_TOTAL
    };
  });

  const sectionWeightTotal = sectionWeights.reduce((sum, section) => sum + section.weight, 0);

  return {
    section_weight_total: sectionWeightTotal,
    is_section_weight_balanced: sectionWeightTotal === REQUIRED_WEIGHT_TOTAL,
    sections: sectionWeights
  };
};

/**
 * Maps a 0-100 score to its band's rating. A fractional score in the gap between
 * two integer bands falls to the lower one. Undefined when there are no bands.
 */
export const mapScoreToRating = (
  bands: RiskBand[] | undefined,
  score: number
): AssessedRiskRating | undefined => {
  if (!bands || bands.length === 0) {
    return undefined;
  }

  const clamped = Math.min(Math.max(score, SCORE_MIN), SCORE_MAX);
  const sorted = [...bands].sort((a, b) => a.min - b.min);
  const match = sorted.filter((band) => clamped >= band.min).pop() ?? sorted[0];

  return match.rating;
};
